<template>
	<div id="faqs">
		<!-- delete dialog -->
		<bee-delete-dialog
			v-model="deleteDialog"
			moduleType="faqs"
			:ids="deleteItemId"
			@on-delete="fetchFAQs"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase error--text">{{$t('delete')}}</span> 
			{{$t('FAQ.the-qa')}}
		</bee-delete-dialog>

		<!-- restore dialog -->
		<bee-restore-dialog
			v-model="restoreDialog"
			moduleType="faqs"
			:ids="restoreItemId"
			@on-restore="fetchFAQs"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase orange--text text-darken-1">{{$t('restore')}}</span> 
			{{$t('FAQ.the-qa')}}
		</bee-restore-dialog>

		<!-- add/edit dialog -->
		<v-dialog
			v-model="dialog"
			width="500"
			:persistent="dialogLoading"
		>
			<v-form ref="form" @submit.prevent="submit" :disabled="dialogLoading">
				<v-card class="rounded-medium">
					<v-card-title
						class="justify-center text-uppercase text-h6"
						:class="{ 'mb-3': !multiLangInputs, 'pb-2': multiLangInputs }"
					>
						{{ editItemId ? $t('edit') : $t('add')}} {{$t('FAQ.qa')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<!-- lang tabs -->
						<v-lang-tabs
							v-if="multiLangInputs"
							v-model="lang"
							:checked-langs="acceptedToSaveLangs"
							:disable-non-selected="dialogLoading"
							background-color="bgColor"
							class="mb-4"
							@change="updateFieldsFromDraft"
						/>

						<!-- inputs -->
						<v-row dense class="my-0">
							<!-- question -->
							<v-col cols="12" class="py-0">
								<v-text-field
									v-model="question"
									:label="$t('inputs.question')"
									outlined
									dense
									class="rounded-small"
									hide-details
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- answer -->
							<v-col cols="12" class="py-0">
								<p class="text-body-1 font-wight-medium text-capitalize my-1">
									{{$t('inputs.answer')}}
								</p>
								<text-editor
									v-model="answer"
									mini-tools
									hide-menubar
									:height="200"
									class="rounded-small mx-n1"
									:error="isSubmitted && !answer"
								></text-editor>
							</v-col>

							<!-- products -->
							<v-col v-if="linkFaqWith.products" sm="6" cols="12" class="py-2">
								<v-autocomplete
									v-model="productId"
									:label="$t('inputs.link-with-product')"
									:items="products"
									item-text="name"
									item-value="id"
									outlined
									dense
									class="rounded-small"
									hide-details
								></v-autocomplete>
							</v-col>

							<!-- news -->
							<v-col v-if="linkFaqWith.news" sm="6" cols="12" class="py-2">
								<v-autocomplete
									v-model="newsId"
									:label="$t('inputs.link-with-news')"
									:items="news"
									item-text="title"
									item-value="id"
									outlined
									dense
									class="rounded-small"
									hide-details
								></v-autocomplete>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn
							type="submit"
							color="primary"
							class="rounded-small"
							:loading="dialogLoading"
							:disabled="dialogLoading"
						>
							{{$t('save')}}
						</v-btn>
						<v-btn
							@click="dialog = false"
							class="rounded-small"
							:disabled="dialogLoading"
						>
							{{$t('cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- filter -->
		<v-card class="rounded-medium mb-3">
			<v-card-text class="pa-2">
				<v-row class="align-center">
					<!-- add -->
					<v-col md="8" cols="12" class="text-sm-start text-center">
						<v-btn
							dark
							color="darkGreen"
							class="rounded-small"
							@click="dialog = true"
						>
							<v-icon>mdi-plus</v-icon>
							{{$t('FAQ.add-new-qa')}}
						</v-btn>
					</v-col>
					<!-- search -->
					<v-col md="4" cols="12" class="d-flex flex-sm-row flex-column align-center text-md-start text-center">
						<v-text-field
							v-model="search"
							:label="$t('search')"
							hide-details
							outlined
							dense
							prepend-inner-icon="mdi-magnify"
							class="rounded-medium align-self-stretch me-sm-6 mb-sm-0 mb-5"
						></v-text-field>
						<v-btn
							:color="isFiltered ? null : 'primary'"
							class="rounded-small"
							@click="isFiltered = !isFiltered"
						>
							{{isFiltered ? $t('cancel') : $t('search')}}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
		
		<!-- table -->
		<v-data-table
			:headers="headers"
			:items="loading ? [] : filteredFAQs"
			:loading="loading"
			dense
			:items-per-page="15"
			class="elevation-3 rounded-medium"
		>
			<template v-slot:item.linkedWith="{ item }">
				<div v-if="linkFaqWith.products && item.productId">
					{{$t('Products.product')}}: {{getProductById(item.productId)?.name ?? '-'}}
				</div>
				<div v-if="linkFaqWith.news && item.newsId">
					{{$t('News.news')}}: {{getNewsById(item.newsId)?.name ?? '-'}}
				</div>
				<span v-if="!item.productId && !item.newsId">-</span>
			</template>

			<template v-slot:item.answer="{ item }">
				<div
					v-html="item.answer"
					class="answer-content ck-content"
				/>
			</template>
			
			<template v-slot:item.actions="{ item }">
				<div class="d-flex justify-center align-center">
					<template v-if="!item.deletedAt">
						<tooltip :text="$t('tooltip.edit')">
							<v-btn
								color="blue darken-4"
								dark
								small
								class="me-1 px-1 flex-shrink-1"
								min-width="20px"
								@click="editItemId = item.id; dialog = true;"
							><v-icon>mdi-pencil</v-icon></v-btn>
						</tooltip>
						<tooltip :text="$t('tooltip.delete')">
							<v-btn
								color="red darken-1"
								dark
								small
								class="px-1"
								min-width="20px"
								@click="deleteItemId = item.id; deleteDialog = true"
							>
								<v-icon>mdi-delete</v-icon>
							</v-btn>
						</tooltip>
					</template>
					<tooltip v-else :text="$t('tooltip.restore')">
						<v-btn
							color="orange darken-1"
							dark
							small
							class="px-1"
							min-width="20px"
							@click="restoreItemId = item.id; restoreDialog = true"
						>
							<v-icon>mdi-restore</v-icon>
						</v-btn>
					</tooltip>
				</div>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import rules from '@/helpers/validation rules'
import { mapGetters, mapState } from 'vuex';
import { defaultLang, langs, multiLangInputs } from '@/configs/translates.config';
import langsDraft from '@/mixins/langs-draft';
import { linkFaqWith } from '@/configs/global.config';

export default {
	name: 'FAQ',
	mixins: [langsDraft],

	data: () => ({
		dialog: false,
		deleteDialog: false,

		editItemId: null,
		deleteItemId: null,

		restoreDialog: false,
		restoreItemId: null,

		isSubmitted: false,

		// filter
		search: null,
		isFiltered: false,

		// configs
		multiLangInputs,
		linkFaqWith,

		// helpers
		loading: false,
		dialogLoading: false,
		lang: defaultLang,
		rules,

		// draft for languages 
		draft: {},
		draftNames: ['question', { name: 'answer', value: '' }],

		// data
		question: null,
		answer: '',
		productId: null,
		newsId: null,
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.editItemId) {
					if (multiLangInputs) {
						this.fetchFAQ();
					} else {
						const faq = this.getFAQById(this.editItemId);
						this.question = faq.question;
						this.answer = faq.answer;
						this.productId = faq.product?.id;
						this.newsId = faq.news?.id;
					}
				}
			} else {
				this.$refs.form.reset();
				this.answer = '';
				this.resetDraft();
				this.editItemId = null;
				this.lang = defaultLang;
			}
		}
	},

	computed: {
		...mapState({
			faqs: state => state.faqs.faqs,
			products: state => state.products.products,
			news: state => state.news.news,
		}),
		...mapGetters({
			getFAQById: 'faqs/getFAQById',
			getProductById: 'products/getProductById',
			getNewsById: 'news/getNewsById',
		}),

		filteredFAQs() {
			return this.filterByName(this.isFiltered, this.faqs, this.search, 'question');
		},

		headers() {
			let headers = [
				{ text: this.$t('headers.question'), value: 'question', align: 'start', class: 'primary--text', cellClass: 'primary--text', width: '30%' },
				{ text: this.$t('headers.answer'), value: 'answer', align: 'start', width: '60%' },
				linkFaqWith.news || linkFaqWith.products
					? { text: this.$t('headers.linked-with'), value: 'linkedWith', align: 'start', sortable: false, width: '60%' }
					: null,
				{ text: this.$t('headers.actions'), value: 'actions', align: 'center', sortable: false, width: 40 },
			];

			headers = headers.filter(Boolean);

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers[i].class ? headers[i].class + ' px-2' : 'px-2';
				headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-2' : 'px-2';
			}

			return headers;
		},

		acceptedToSaveLangs() {
			const acceptedLangs = langs.reduce((res, a) => { res[a.key] = false; return res }, {})
			langs.forEach(lang => {
				if (this.draft.question[lang.key] && this.draft.answer[lang.key]) {
					acceptedLangs[lang.key] = true;
				}
			});
			return acceptedLangs;
		}
	},

	methods: {
		async submit() {
			this.isSubmitted = true;
			if (this.$refs.form.validate()) {
				this.isSubmitted = false;
				this.dialogLoading = true;
				const promises = [];
				let itemData = null;

				const submitRequest = (
					{ question, answer, lang },
					id = this.editItemId
				) => {
					const actionName = id ? 'update' : 'create';
					return this.$store.dispatch(`faqs/${actionName}`, {
						id: id,
						question: question,
						answer: answer,
						productId: this.productId,
						newsId: this.newsId,
						acceptLanguage: lang
					});
				};

				const filteredLangs = langs.filter(c => multiLangInputs || c.key === defaultLang);
				for (let index = 0; index < filteredLangs.length; index++) {
					const lang = filteredLangs[index].key;

					const question = this.draft.question[lang];
					const answer = this.draft.answer[lang];

					const dataDueLang = { question, answer, lang };
					
					if (question && answer) {
						if (!itemData) {
							await submitRequest(dataDueLang).then(data => { itemData = data });
						} else {
							promises.push(submitRequest(dataDueLang, itemData.id));
						}
					}
				}
				
				Promise.all(promises).then((data) => {
					const message =
						this.editItemId
							? this.$t('$-is-updated-successfully', { name: this.$t('FAQ.qa') })
							: this.$t('$-is-created-successfully', { name: this.$t('FAQ.qa') });
					this.$eventBus.$emit('show-snackbar', false, message);

					this.fetchFAQs();
					this.dialog = false;
				}).catch(() => {
					this.editItemId = itemData.id;
				}).finally(() => {
					this.dialogLoading = false;
				})
			}
		},

		fetchFAQs(isFirstLoad = false) {
			this.loading = true;
			this.$store.dispatch('faqs/fetchAll')
				.finally(() => {
					if (!isFirstLoad) this.loading = false;
				})
		},

		fetchFAQ() {
			this.dialogLoading = true;
			const id = this.getFAQById(this.editItemId).id;

			const promises = [];
			langs.filter(c => multiLangInputs || c.key === defaultLang).forEach((c, index) => {
				promises.push(
					this.$store.dispatch('faqs/fetchById', { id, acceptLanguage: c.key })
						.then((data) => {
							if (index === 0) {
								this.productId = data.product?.id;
								this.newsId = data.news?.id;
							}
							this.draft.question[c.key] = data.question;
							this.draft.answer[c.key] = data.answer;
						})
				);
			});

			Promise.all(promises).then(() => {
				this.question = this.draft.question[this.lang];
				this.answer = this.draft.answer[this.lang];
			}).finally(() => {
				this.dialogLoading = false;
			})
		}
	},

	created() {
		this.loading = true;
		Promise.all([
			linkFaqWith.products && !this.products.length ? this.$store.dispatch('products/fetchAll', {}) : null,
			linkFaqWith.news && !this.news.length ? this.$store.dispatch('news/fetchAll', {}) : null,
			this.fetchFAQs()
		]).finally(() => {
			this.loading = false;
		});
	}
}
</script>

<style lang="scss">
#faqs {
	.answer-content {
		p {
			margin: 0;
		}
	}
}
</style>